import React, { Component, useEffect } from 'react'
import memoize from 'memoizee'
import { Route, Switch, withRouter, Redirect } from 'react-router-dom'
import loadable from '@loadable/component'

import AuthorizationRequired from 'cityads-v3/containers/Auth/AuthorizationRequired'
import { amplitudeSendEvent } from 'cityads-v3/utils/counterUtils'

import Account from './account/Account'
import Api from './api'
import Coupons from './coupons/Coupons'
import Firano from './firano/Firano'
import Loading from 'cityads-v3/containers/app/Loading'
import NotFound from 'cityads-v3/containers/misc/NotFound'
import Offers from './offers/Offers'
import { OfferTabValue } from './offers/OfferFilters.hooks'
import Profile from './profile/Profile'

const DashboardContainer = loadable(() => import(/* webpackChunkName: "dashboard" */'cityads-v3/containers/webmaster/dashboard/Dashboard'), {
  fallback: <Loading />,
})

const OfferPageContainer = loadable(() => import(/* webpackChunkName: "offerpage" */'cityads-v3/containers/webmaster/offerPage/OfferPage'), {
  fallback: <Loading />,
})

const SwaggerContainer = loadable(() => import(/* webpackChunkName: "swagger" */'cityads-v3/containers/webmaster/swagger'), {
  fallback: <Loading />,
})

const ApiStatisticsContainer = loadable(() => import(/* webpackChunkName: "swagger" */'cityads-v3/containers/webmaster/api/Statistics'), {
  fallback: <Loading />,
})

const ZaleyCashContainer = loadable(() => import(/* webpackChunkName: "zaleycash" */'cityads-v3/containers/webmaster/zaleycash/Container'), {
  fallback: <Loading />,
})

const PaymentsContainer = loadable(() => import(/* webpackChunkName: "payments" */'cityads-v3/containers/webmaster/payments/Payments'), {
  fallback: <Loading />,
})

const ToolsContainer = loadable(() => import(/* webpackChunkName: "tools" */'cityads-v3/containers/webmaster/tools/Tools'), {
  fallback: <Loading />,
})

const GoodsContainer = loadable(() => import(/* webpackChunkName: "goods" */'cityads-v3/containers/webmaster/goods/Goods'), {
  fallback: <Loading />,
})

const FiranoRequestsContainer = loadable(() => import(/* webpackChunkName: "firanorequests" */'cityads-v3/containers/webmaster/firanoRequests/FiranoRequests'), {
  fallback: <Loading />,
})

const AgentaContainer = loadable(() => import(/* webpackChunkName: "agenta" */'cityads-v3/containers/webmaster/agenta/Agenta'), {
  fallback: <Loading />,
})

const ReferralsContainer = loadable(() => import(/* webpackChunkName: "referrals" */'cityads-v3/containers/webmaster/referrals/MyReferrals'), {
  fallback: <Loading />,
})

const ModerationContainer = loadable(() => import(/* webpackChunkName: "moderation" */'cityads-v3/containers/webmaster/moderation/Moderation'), {
  fallback: <Loading />,
})

const AdvertisingMarkingContainer = loadable(() => import(/* webpackChunkName: "advertisingmarking" */'cityads-v3/containers/webmaster/advertisingMarking/AdvertisingMarking'), {
  fallback: <Loading />,
})

const RequestsHistoryContainer = loadable(() => import(/* webpackChunkName: "requestshistory" */'cityads-v3/containers/webmaster/requestsHistory/RequestsHistory'), {
  fallback: <Loading />,
})

// Without memoization, every render of Webmaster creates new wrapper classes, causing remounts
const officePageWrapper = memoize(function (WrappedComponent, pageName) {
  const NewComponent = props => {
    useEffect(() => {
      amplitudeSendEvent('makeActions', 'd-v1-e10', 'office', 'tab_view', { tab_name: pageName })
    }, [])

    return (
      <WrappedComponent {...props} />
    )
  }

  NewComponent.displayName = `OfficePageWrapper_${pageName}`

  return NewComponent
})

// Without memoization, every render of Webmaster creates new wrapper classes, causing remounts
const firanoPageWrapper = memoize(function (WrappedComponent, pageName) {
  const NewComponent = props => {
    useEffect(() => {
      amplitudeSendEvent('makeActions', 'd-v1-e11', 'firano', 'tab_view', { tab_name: pageName })
    }, [])

    return (
      <WrappedComponent {...props} />
    )
  }

  NewComponent.displayName = `FiranoPageWrapper_${pageName}`

  return NewComponent
})

@withRouter
export default class Webmaster extends Component {

  getRoutes() {
    return [
      {
        path: '/publisher/offers/favorites',
        render: props => <Offers {...props} tabValue={OfferTabValue.FILTER_TYPE_FAVORITES} />,
      },
      {
        path: '/publisher/offers',
        render: props => <Offers {...props} tabValue={OfferTabValue.FILTER_TYPE_ALL} />,
      },
      {
        path: '/publisher/coupons',
        render: props => <AuthorizationRequired allowedUserRoleCode="W"><Coupons {...props} /></AuthorizationRequired>,
        onlyLoggedIn: true,
      },
      {
        path: '/publisher/offer/:id/:tab?',
        component: OfferPageContainer,
      },
      {
        path: '/publisher/tools/:toolCode?',
        component: ToolsContainer,
        onlyLoggedIn: true,
      },
      {
        path: '/publisher/goods/:goodCode?',
        component: GoodsContainer,
        onlyLoggedIn: true,
      },
      {
        path: '/publisher/lostorders',
        render: props => <Redirect to={'/publisher/tools/lostorders'} />,
        onlyLoggedIn: true,
      },
      {
        path: '/publisher/trackers',
        render: props => <Redirect to={'/publisher/tools/trackers'} />,
        onlyLoggedIn: true,
      },
      {
        path: '/publisher/profile',
        component: officePageWrapper(Profile, 'profile'),
        onlyLoggedIn: true,
      },
      {
        path: '/publisher/account',
        component: officePageWrapper(Account, 'account'),
        onlyLoggedIn: true,
      },
      {
        path: '/publisher/api/description',
        component: officePageWrapper(SwaggerContainer, 'api_description'),
      },
      {
        path: '/publisher/api/statistics',
        component: officePageWrapper(ApiStatisticsContainer, 'api_statistics'),
      },
      {
        path: '/publisher/api',
        component: officePageWrapper(Api, 'api'),
        onlyLoggedIn: true,
      },
      {
        path: '/publisher/finance/requests/:subPage?',
        render: props => <Redirect to={'/publisher/firano/requests/' + (props.match.params.subPage ?? '')} />
      },
      {
        path: '/publisher/firano/requests/:subPage?',
        component: firanoPageWrapper(FiranoRequestsContainer, 'firano_requests'),
        onlyLoggedIn: true,
      },
      {
        path: '/publisher/finance',
        render: props => <Redirect to={'/publisher/firano'} />
      },
      {
        path: '/publisher/firano',
        component: firanoPageWrapper(Firano, 'firano'),
        onlyLoggedIn: true,
      },
      {
        path: '/publisher/zaleycash',
        component: officePageWrapper(ZaleyCashContainer, 'zaleycash'),
        onlyLoggedIn: true,
      },
      {
        path: '/publisher/payments',
        component: officePageWrapper(PaymentsContainer, 'payments'),
        onlyLoggedIn: true,
      },
      {
        path: '/publisher/dashboard',
        component: officePageWrapper(DashboardContainer, 'dashboard'),
        onlyLoggedIn: true,
      },
      {
        path: '/publisher/agenta',
        component: AgentaContainer,
        onlyLoggedIn: true
      },
      {
        path: '/publisher/referrals',
        component: officePageWrapper(ReferralsContainer, 'referrals'),
        onlyLoggedIn: true,
      },
      {
        path: '/publisher/moderation',
        component: ModerationContainer,
        onlyLoggedIn: true
      },
      {
        path: '/publisher/advertisingMarking',
        component: AdvertisingMarkingContainer,
        onlyLoggedIn: true
      },
      {
        path: '/publisher/requestsHistory',
        component: RequestsHistoryContainer,
        onlyLoggedIn: true
      },
    ]
  }

  render() {
    return (
      <>
        <Switch>
          {this.getRoutes().map(({ onlyLoggedIn, path, component: Component, ...rest }, index) =>
            (Component && onlyLoggedIn) ?
              (
                <Route key={index} path={path} {...rest} render={routeProps => (
                  <AuthorizationRequired allowedUserRoleCode="W">
                    <Component {...routeProps} />
                  </AuthorizationRequired>
                )} />
              ) :
              (
                <Route key={index} path={path} component={Component} {...rest} />
              )
          )}
          <Route component={NotFound} />
        </Switch>
      </>
    )
  }
}
